import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  LocalShipping as LocalShippingIcon,
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Update as UpdateIcon,
  DirectionsRun as DirectionsRunIcon
} from '@mui/icons-material';
import apiService from '../../services/apiService';

const ShipmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState(null);
  const [shipmentStates, setShipmentStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusComments, setStatusComments] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    const fetchShipmentData = async () => {
      try {
        setLoading(true);
        
        // Obtener datos del envío
        const shipmentData = await apiService.getMoneyShipment(id);
        setShipment(shipmentData);
        
        // Obtener historial de estados
        try {
          const statesData = await apiService.getMoneyShipmentStates(id);
          setShipmentStates(statesData);
        } catch (err) {
          console.error('Error al cargar estados del envío:', err);
        }
        
      } catch (err) {
        console.error('Error al cargar datos del envío:', err);
        setError('Error al cargar los datos del envío. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchShipmentData();
  }, [id]);

  const handleBack = () => {
    navigate('/shipments');
  };

  const handleOpenStatusDialog = () => {
    setOpenStatusDialog(true);
  };

  const handleCloseStatusDialog = () => {
    setOpenStatusDialog(false);
    setNewStatus('');
    setStatusComments('');
  };

  const handleChangeStatus = async () => {
    if (!newStatus) return;
    
    try {
      setUpdatingStatus(true);
      await apiService.changeMoneyShipmentStatus(id, newStatus, statusComments);
      
      // Recargar datos
      const shipmentData = await apiService.getMoneyShipment(id);
      setShipment(shipmentData);
      
      const statesData = await apiService.getMoneyShipmentStates(id);
      setShipmentStates(statesData);
      
      handleCloseStatusDialog();
    } catch (err) {
      console.error('Error al cambiar estado del envío:', err);
      setError('Error al cambiar el estado del envío. Por favor, intente nuevamente.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusChip = (status) => {
    let color;
    let label;
    
    switch (status) {
      case 'pending':
        color = 'warning';
        label = 'Pendiente';
        break;
      case 'in_transit':
        color = 'info';
        label = 'En Tránsito';
        break;
      case 'finished':
        color = 'success';
        label = 'Finalizado';
        break;
      case 'canceled':
        color = 'default';
        label = 'Cancelado';
        break;
      case 'failed':
        color = 'error';
        label = 'Fallido';
        break;
      default:
        color = 'default';
        label = status;
    }
    
    return <Chip label={label} color={color} />;
  };

  const formatCurrency = (amount, currency) => {
    const formatter = new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: currency === 'PEN' ? 'PEN' : 'USD',
      minimumFractionDigits: 2
    });
    
    return formatter.format(amount);
  };

  const getStatusSteps = () => {
    const steps = [
      { status: 'pending', label: 'Pendiente', icon: <UpdateIcon /> },
      { status: 'in_transit', label: 'En Tránsito', icon: <DirectionsRunIcon /> },
      { status: 'finished', label: 'Finalizado', icon: <CheckCircleIcon /> }
    ];
    
    // Si está cancelado o fallido, mostrar un paso especial
    if (shipment.status === 'canceled') {
      return [
        { status: 'pending', label: 'Pendiente', icon: <UpdateIcon /> },
        { status: 'canceled', label: 'Cancelado', icon: <CancelIcon /> }
      ];
    } else if (shipment.status === 'failed') {
      return [
        { status: 'pending', label: 'Pendiente', icon: <UpdateIcon /> },
        { status: 'in_transit', label: 'En Tránsito', icon: <DirectionsRunIcon /> },
        { status: 'failed', label: 'Fallido', icon: <CancelIcon /> }
      ];
    }
    
    return steps;
  };

  const getActiveStep = () => {
    const statusMap = {
      'pending': 0,
      'in_transit': 1,
      'finished': 2,
      'canceled': 1, // En caso de cancelación, el paso activo es 1
      'failed': 2 // En caso de fallo, el paso activo es 2
    };
    
    return statusMap[shipment.status] || 0;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !shipment) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">{error || 'Envío no encontrado'}</Typography>
      </Box>
    );
  }

  const steps = getStatusSteps();
  const activeStep = getActiveStep();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Volver
          </Button>
          <Typography variant="h4">
            Envío de Dinero #{shipment.id}
          </Typography>
        </Box>
        {shipment.status !== 'canceled' && shipment.status !== 'finished' && shipment.status !== 'failed' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<UpdateIcon />}
            onClick={handleOpenStatusDialog}
          >
            Cambiar Estado
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', mr: 2 }}>
            <LocalShippingIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h5">
              Envío de {formatCurrency(shipment.amount, shipment.currency_type)}
            </Typography>
            <Box display="flex" alignItems="center" mt={0.5}>
              {getStatusChip(shipment.status)}
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Creado: {new Date(shipment.created_at).toLocaleString()}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" mt={0.5}>
              <Chip 
                icon={<DirectionsRunIcon />} 
                label={shipment.user ? `Portavalor: ${shipment.user.first_name} ${shipment.user.father_last_name}` : 'Sin portavalor asignado'} 
                color="primary" 
                variant="outlined" 
                sx={{ mt: 1 }}
              />
            </Box>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step) => (
            <Step key={step.status}>
              <StepLabel
                StepIconProps={{
                  icon: step.icon
                }}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="Sucursal Origen" 
                avatar={<Avatar><BusinessIcon /></Avatar>}
              />
              <Divider />
              <CardContent>
                {shipment.branch_origin ? (
                  <>
                    <Typography variant="h6">
                      {shipment.branch_origin?.branch_name || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {shipment.branch_origin?.district_name ? 
                        `${shipment.branch_origin.district_name}, ${shipment.branch_origin.province_name}, ${shipment.branch_origin.department_name}` : 
                        'Sin ubicación registrada'}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Información de la sucursal no disponible
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="Sucursal Destino" 
                avatar={<Avatar><BusinessIcon /></Avatar>}
              />
              <Divider />
              <CardContent>
                {shipment.branch_destiny ? (
                  <>
                    <Typography variant="h6">
                      {shipment.branch_destiny?.branch_name || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {shipment.branch_destiny?.district_name ? 
                        `${shipment.branch_destiny.district_name}, ${shipment.branch_destiny.province_name}, ${shipment.branch_destiny.department_name}` : 
                        'Sin ubicación registrada'}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Información de la sucursal no disponible
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Información Financiera" 
                avatar={<Avatar><AttachMoneyIcon /></Avatar>}
              />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Monto
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(shipment.amount, shipment.currency_type)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Moneda
                    </Typography>
                    <Typography variant="h6">
                      {shipment.currency_type === 'PEN' ? 'Soles (PEN)' : 'Dólares (USD)'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {shipment.comments && (
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Comentarios" />
                <Divider />
                <CardContent>
                  <Typography variant="body1">
                    {shipment.comments}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Paper>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card>
            <CardHeader 
              title="Información de Portavalor" 
              avatar={<Avatar><DirectionsRunIcon /></Avatar>}
            />
            <Divider />
            <CardContent>
              {shipment.user ? (
                <>
                  <Typography variant="h6">
                    {`${shipment.user?.first_name || ''} ${shipment.user?.father_last_name || ''}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Documento: {shipment.user?.document_type || ''} {shipment.user?.document_number || 'No registrado'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Teléfono: {shipment.user?.phone_prefix || ''} {shipment.user?.phone_number || 'No registrado'}
                  </Typography>
                </>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Información del portavalor no disponible
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card>
            <CardHeader 
              title="Historial de Estados" 
              avatar={<Avatar><HistoryIcon /></Avatar>}
            />
            <Divider />
            <CardContent>
              {shipmentStates.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hay historial de estados disponible
                </Typography>
              ) : (
                <List>
                  {shipmentStates.map((state) => (
                    <ListItem key={state.id}>
                      <ListItemIcon>
                        {state.status === 'pending' && <UpdateIcon color="warning" />}
                        {state.status === 'in_transit' && <DirectionsRunIcon color="info" />}
                        {state.status === 'canceled' && <CancelIcon color="default" />}
                        {state.status === 'finished' && <CheckCircleIcon color="success" />}
                        {state.status === 'failed' && <CancelIcon color="error" />}
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Box display="flex" alignItems="center">
                            {getStatusChip(state.status)}
                            <Typography variant="body2" sx={{ ml: 2 }}>
                              {new Date(state.changed_at).toLocaleString()}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Usuario: {state.user.first_name} {state.user.father_last_name}
                            </Typography>
                            {state.comments && (
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Comentarios: {state.comments}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Diálogo para cambiar estado */}
      <Dialog open={openStatusDialog} onClose={handleCloseStatusDialog}>
        <DialogTitle>Cambiar Estado del Envío</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Seleccione el nuevo estado para el envío y agregue comentarios si es necesario.
          </DialogContentText>
          <FormControl fullWidth margin="normal">
            <InputLabel id="new-status-label">Nuevo Estado</InputLabel>
            <Select
              labelId="new-status-label"
              value={newStatus}
              label="Nuevo Estado"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              {/* Mostrar opciones según el estado actual */}
              {shipment.status === 'pending' && [
                <MenuItem key="transit" value="in_transit">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip size="small" label="En Tránsito" color="info" sx={{ mr: 1 }} />
                    En Tránsito
                  </Box>
                </MenuItem>,
                <MenuItem key="canceled" value="canceled">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip size="small" label="Cancelado" color="default" sx={{ mr: 1 }} />
                    Cancelado
                  </Box>
                </MenuItem>
              ]}
                
              {shipment.status === 'in_transit' && [
                <MenuItem key="finished" value="finished">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip size="small" label="Finalizado" color="success" sx={{ mr: 1 }} />
                    Finalizado
                  </Box>
                </MenuItem>,
                <MenuItem key="failed" value="failed">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip size="small" label="Fallido" color="error" sx={{ mr: 1 }} />
                    Fallido
                  </Box>
                </MenuItem>,
                <MenuItem key="canceled2" value="canceled">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip size="small" label="Cancelado" color="default" sx={{ mr: 1 }} />
                    Cancelado
                  </Box>
                </MenuItem>
              ]}
                
              {/* Permitir regresar a "En Tránsito" desde otros estados si es necesario */}
              {(shipment.status === 'failed' || shipment.status === 'canceled') && [
                <MenuItem key="transit2" value="in_transit">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip size="small" label="En Tránsito" color="info" sx={{ mr: 1 }} />
                    En Tránsito
                  </Box>
                </MenuItem>,
                <MenuItem key="finished2" value="finished">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip size="small" label="Finalizado" color="success" sx={{ mr: 1 }} />
                    Finalizado
                  </Box>
                </MenuItem>
              ]}
            </Select>
          </FormControl>
          <TextField
            margin="normal"
            label="Comentarios"
            fullWidth
            multiline
            rows={3}
            value={statusComments}
            onChange={(e) => setStatusComments(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>Cancelar</Button>
          <Button 
            onClick={handleChangeStatus} 
            variant="contained" 
            color="primary"
            disabled={!newStatus || updatingStatus}
          >
            {updatingStatus ? <CircularProgress size={24} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShipmentDetail; 